import { Button, Col, DatePicker, Form, Input, Row, Spin } from 'antd';
import '../../styles/_orgs.scss';
import { useCreateOrganizationMutation } from '../../features/organization/organizationService';
import useOrderToast from '../../hooks/useOrderToast';

type FieldType = {
    organizationName?: string;
    organizationId?: string;
    organizationCode?: string;
    effectiveDate?: string;
    implementationDate?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    phoneNumber?: string;
    adminEmailAddress?: string;
};

type Props = {
    setOrgModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const ExtraFieldLabel = ({ title }: { title: string }) => {
    return <span className="ms-3 text-semi-black-xs">{title}</span>;
};
const ValidationMessage = ({ message }: { message: string }) => {
    return <span className="ms-3">{message}</span>;
};

const AddNewOrganizationForm : React.FC<Props> =({setOrgModalOpen}) => {
    const [form] = Form.useForm();
    const [createOrganization, { isLoading }] = useCreateOrganizationMutation();
    const { toast } = useOrderToast();

    const handleSubmit = async () => {
        const values = await form.validateFields();
        try {
            const response: any = await createOrganization({
                    ...values,
                    country: 'United States of America',
            });
            if (response && response.data && response.data.organizationId) {
                toast('The organization is successfully created.', 'success', 2);
                setOrgModalOpen(false);
                form.resetFields();
            }
            else{
                toast('The organization creation is failed. Please try again.', 'error', 2);
            }
        } catch (error) {
            toast('The organization creation is failed. Please try again.', 'error', 2);
        }
    };
    return (
        <Spin spinning={isLoading}>
            <Form
            form={form}
            name="basic"
            layout="horizontal"
            autoComplete="off"
            className="orgs-new-team-form"
        >
            <Row>
                <Col span={24}>
                    <Form.Item<FieldType>
                        label={'Organization Name'}
                        name="organizationName"
                        labelCol={{ span: 4 }}
                        wrapperCol={{ span: 11 }}
                        rules={[
                            {
                                required: true,
                                message: (
                                    <ValidationMessage message="Please enter Organization Name" />
                                ),
                            },
                        ]}
                        extra={
                            <ExtraFieldLabel title="Organization name should align with Scripture and RxLogic" />
                        }
                    >
                        <Input
                            placeholder="Enter Organization Name"
                            className="ms-3 bg-light-blue-thin rounded-1"
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={12}>
                    <Form.Item
                        label="Organization ID"
                        name="organizationId"
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 14 }}
                        rules={[
                            {
                                required: true,
                                message: (
                                    <ValidationMessage message="Please enter Organization ID" />
                                ),
                            },
                        ]}
                        extra={
                            <ExtraFieldLabel title="Organization ID should align with RxLogic" />
                        }
                    >
                        <Input
                            placeholder="Enter Organization ID"
                            className="ms-3 bg-light-blue-thin rounded-1"
                        />
                    </Form.Item>
                </Col>

                <Col span={12}>
                    <Form.Item
                        label="Organization Code"
                        name="organizationCode"
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                        rules={[
                            {
                                required: true,
                                message: (
                                    <ValidationMessage message="Please enter Organization Code" />
                                ),
                            },
                        ]}
                        extra={
                            <ExtraFieldLabel title="Organization code is unique to NaviScript" />
                        }
                    >
                        <Input
                            placeholder="Enter Organization Code"
                            className="ms-3 bg-light-blue-thin rounded-1"
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={12}>
                    <Form.Item<FieldType>
                        label={'Effective Date'}
                        name="effectiveDate"
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 14 }}
                        rules={[
                            {
                                required: true,
                                message: (
                                    <ValidationMessage message="Please enter Effective Date" />
                                ),
                            },
                        ]}
                    >
                        <DatePicker
                            getPopupContainer={(trigger) =>
                                trigger.parentElement as HTMLElement
                            }
                            className="ms-3 bg-light-blue-thin rounded-1 w-100"
                            placeholder="Select Effective Date"
                            format={'MM-DD-YYYY'}
                        />
                    </Form.Item>
                </Col>

                <Col span={12}>
                    <Form.Item<FieldType>
                        label={'Hospice Implementation Date'}
                        name="implementationDate"
                        labelCol={{ span: 11 }}
                        wrapperCol={{ span: 14 }}
                        rules={[
                            {
                                required: true,
                                message: (
                                    <ValidationMessage message="Please enter Implementation Date" />
                                ),
                            },
                        ]}
                    >
                        <DatePicker
                            getPopupContainer={(trigger) =>
                                trigger.parentElement as HTMLElement
                            }
                            className="ms-3 bg-light-blue-thin rounded-1 w-100"
                            placeholder="Select Hospice Implementation Date"
                            format={'MM-DD-YYYY'}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Form.Item<FieldType>
                        label={'Address Line 1'}
                        name="addressLine1"
                        labelCol={{ span: 4 }}
                        wrapperCol={{ span: 20 }}
                        rules={[
                            {
                                required: true,
                                message: (
                                    <ValidationMessage message="Please enter Address Line 1" />
                                ),
                            },
                        ]}
                        extra={
                            <ExtraFieldLabel title="Street Address, P.O. Box, Company Name, C/O" />
                        }
                    >
                        <Input
                            className="new-team-modal-input ms-3 bg-light-blue-thin rounded-1"
                            placeholder="Enter Address Line 1"
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Form.Item<FieldType>
                        label={'Address Line 2'}
                        name="addressLine2"
                        labelCol={{ span: 4 }}
                        wrapperCol={{ span: 20 }}
                        extra={
                            <ExtraFieldLabel title="Apartment, Suite, Unit, Building, Floor, Etc." />
                        }
                    >
                        <Input
                            className="new-team-modal-input ms-3 bg-light-blue-thin rounded-1"
                            placeholder="Enter Address Line 2"
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={12}>
                    <Form.Item
                        label="City"
                        name="city"
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 14 }}
                        rules={[
                            {
                                required: true,
                                message: (
                                    <ValidationMessage message="Please enter City" />
                                ),
                            },
                        ]}
                    >
                        <Input
                            placeholder="Enter City"
                            className="ms-3 bg-light-blue-thin rounded-1"
                        />
                    </Form.Item>
                </Col>

                <Col span={12}>
                    <Form.Item
                        label="State/Province/Region"
                        name="state"
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                        rules={[
                            {
                                required: true,
                                message: (
                                    <ValidationMessage message="Please enter State/Province/Region" />
                                ),
                            },
                        ]}
                    >
                        <Input
                            placeholder="Enter State/Province/Region"
                            className="ms-3 bg-light-blue-thin rounded-1"
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={12}>
                    <Form.Item
                        label="ZIP/Postal Code"
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 14 }}
                        name="zipCode"
                        rules={[
                            {
                                required: true,
                                message: (
                                    <ValidationMessage message="Please enter ZIP/Postal Code" />
                                ),
                            },
                        ]}
                    >
                        <Input
                            placeholder="Enter ZIP/Postal Code"
                            className="ms-3 bg-light-blue-thin rounded-1"
                        />
                    </Form.Item>
                </Col>

                <Col span={12}>
                    <Form.Item
                        label="Country"
                        name="country"
                        labelCol={{ span: 8 }}
                        wrapperCol={{ span: 16 }}
                    >
                        <Input
                            disabled
                            placeholder="United States of America"
                            className="ms-3 bg-light-blue-thin rounded-1"
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Form.Item<FieldType>
                        label={'Phone Number'}
                        name="phoneNumber"
                        labelCol={{ span: 4 }}
                        wrapperCol={{ span: 7 }}
                        rules={[
                            {
                                required: true,
                                message: (
                                    <ValidationMessage message="Please enter Phone Number" />
                                ),
                            },
                        ]}
                    >
                        <Input
                            className="ms-3 bg-light-blue-thin rounded-1"
                            maxLength={10}
                            placeholder="(_ _ _) (_ _ _) (_ _ _ _)"
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Form.Item<FieldType>
                        label={'Admin Email Address'}
                        name="adminEmailAddress"
                        labelCol={{ span: 4 }}
                        wrapperCol={{ span: 7 }}
                        rules={[
                            {
                                required: true,
                                message: (
                                    <ValidationMessage message="Please enter Admin Email Address" />
                                ),
                            },
                        ]}
                    >
                        <Input
                            placeholder="Enter Email Address"
                            className="ms-3 bg-light-blue-thin rounded-1"
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={4} offset={4}>
                    <Button
                        type="primary"
                        className="w-75 ms-3 bg-light-blue border-0"
                        shape="round"
                        onClick={handleSubmit}
                    >
                        Save
                    </Button>
                </Col>
            </Row>
        </Form>
        </Spin>
        
    );
};

export default AddNewOrganizationForm;
